import {
    Fragment,
    useState,
    useRef,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IInputGroupProps from "./type";
import type {
    InputGroupTextHint
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import InputGroupAddon from "./components/addon";
import InputGroupButton from "./components/button";
import InputGroupInput from "./components/input";
import InputGroupSpinner from "./components/spinner";
import InputGroupText from "./components/text";
import InputGroupTextArea from "./components/textArea";
import {
    InputGroupContext
} from "./context";
import {
    getInputGroupParts
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type FieldInputRenderPropsType
} from "../fieldControl/type";
import type {
    TextInputInstance
} from "../textInput/type";
import {
    getFieldSizeKey,
    getFieldSize,
    getFieldIDs
} from "../fieldControl/stylesheet";
import {
    getTextAreaInputCounter
} from "../textAreaInput/stylesheet";
import {
    uuid
} from "../../utils";
import FieldControl from "../fieldControl";

const InputGroup: FC<IInputGroupProps> = ({
    isShowHintTextIcon = false,
    isShowCounter = false,
    isDisabled = false,
    isOptional = false,
    isReadOnly = false,
    isRequired = false,
    isLoading = false,
    type = "neutral",
    spreadBehaviour,
    customLocalize,
    hintTextIcon,
    customTheme,
    maxLength,
    children,
    hintText,
    style,
    title,
    size,
    id,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const inputRef = useRef<TextInputInstance | null>(null);

    const generatedID = useRef(uuid());

    const [
        textHints,
        setTextHints
    ] = useState<Array<InputGroupTextHint>>([]);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        length,
        setLength
    ] = useState(0);

    const inputID = id ?? generatedID.current;

    const parts = getInputGroupParts({
        children
    });

    const currentSizeKey = getFieldSizeKey({
        isActionable: parts.isActionable,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const currentCounter = isShowCounter && maxLength ? getTextAreaInputCounter({
        locale: activeLocale,
        maxLength,
        length
    }) : undefined;

    const fieldIDs = getFieldIDs({
        inputID
    });

    const {
        textAreaSpinner: textAreaSpinnerDynamicStyle,
        stack: stackDynamicStyle
    } = useStyles({
        currentSize,
        spaces
    });

    const isOver = !!currentCounter?.isOver;

    const isWarning = !!currentCounter?.isWarning;

    const isFull = !!currentCounter?.isFull;

    const getSubTitle = () => {
        return isOptional && !isRequired ? localize("is-optional") : undefined;
    };

    const focusInput = () => {
        inputRef.current?.focus();
    };

    const registerText = (hint: InputGroupTextHint) => {
        setTextHints((previousHints) => {
            const hintIndex = previousHints.findIndex((item) => item.id === hint.id);

            if(hintIndex === -1) {
                return [
                    ...previousHints,
                    hint
                ];
            }

            if(previousHints[hintIndex]?.text === hint.text) {
                return previousHints;
            }

            return previousHints.map((item) => item.id === hint.id ? hint : item);
        });
    };

    const unregisterText = (textID: string) => {
        setTextHints((previousHints) => previousHints.filter((item) => item.id !== textID));
    };

    const onInputFocus = () => {
        setIsFocused(true);
    };

    const onInputBlur = () => {
        setIsFocused(false);
    };

    const renderLoadingIndicator = () => {
        if(!isLoading || parts.isActionable) {
            return null;
        }

        return <InputGroupSpinner />;
    };

    const renderTextAreaLoadingIndicator = () => {
        if(!isLoading) {
            return null;
        }

        return <InputGroupSpinner
            style={textAreaSpinnerDynamicStyle}
        />;
    };

    const renderContent = () => {
        if(parts.isMultiline) {
            return <View
                style={[
                    stylesheet.stack,
                    stackDynamicStyle
                ]}
            >
                {parts.blockStart}
                <View
                    style={stylesheet.textAreaContainer}
                >
                    {parts.textArea}
                    {renderTextAreaLoadingIndicator()}
                </View>
                {parts.blockEnd}
            </View>;
        }

        return <Fragment>
            {parts.leadingAttached}
            {parts.rowItems}
            {renderLoadingIndicator()}
            {parts.trailingAttached}
        </Fragment>;
    };

    const renderInput = ({
        placeholderTextColor,
        accessibilityProps,
        style: inputStyle
    }: FieldInputRenderPropsType) => {
        return <InputGroupContext.Provider
            value={{
                counterText: parts.isMultiline ? currentCounter?.text : undefined,
                inputAccessibilityProps: accessibilityProps,
                isCounterLive: !!currentCounter?.isLive,
                counterID: `${inputID}-counter`,
                isMultiline: parts.isMultiline,
                onInputLengthChange: setLength,
                isCounterWarning: isWarning,
                isCounterFull: isFull,
                isCounterOver: isOver,
                placeholderTextColor,
                size: currentSizeKey,
                customLocalize,
                unregisterText,
                onInputFocus,
                registerText,
                currentSize,
                customTheme,
                onInputBlur,
                focusInput,
                inputStyle,
                isDisabled,
                isReadOnly,
                isLoading,
                textHints,
                inputRef,
                inputID
            }}
        >
            {renderContent()}
        </InputGroupContext.Provider>;
    };

    return <FieldControl
        {...props}
        aria-labelledby={title && (parts.isActionable || parts.isMultiline) ? fieldIDs.labelID : props["aria-labelledby"]}
        spreadBehaviour={spreadBehaviour ?? (parts.isMultiline ? "stretch" : "baseline")}
        role={parts.isActionable || parts.isMultiline ? "group" : props.role}
        counterText={parts.isMultiline ? undefined : currentCounter?.text}
        isCounterLive={!!currentCounter?.isLive}
        isShowHintTextIcon={isShowHintTextIcon}
        customLocalize={customLocalize}
        type={isOver ? "danger" : type}
        isCounterWarning={isWarning}
        hintTextIcon={hintTextIcon}
        onFocusRequest={focusInput}
        customTheme={customTheme}
        renderInput={renderInput}
        subTitle={getSubTitle()}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        isCounterOver={isFull}
        isFocused={isFocused}
        size={currentSizeKey}
        hintText={hintText}
        isLoading={false}
        inputID={inputID}
        title={title}
        style={style}
    />;
};
export default Object.assign(InputGroup, {
    TextArea: InputGroupTextArea,
    Button: InputGroupButton,
    Addon: InputGroupAddon,
    Input: InputGroupInput,
    Text: InputGroupText
});
